import { useEffect, useRef } from 'react';

/**
 * 앱이 포그라운드로 복귀할 때 데이터를 Refetch하는 커스텀 훅.
 * @param {Function} callback 데이터 로드 함수
 * @param {number} cooldownMs 중복 호출 방지 쿨다운 시간 (기본값 5초)
 */
export const useForegroundRefetch = (callback, cooldownMs = 5000) => {
  const lastRefetchTime = useRef(0);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const tryRefetch = () => {
      const now = Date.now();
      if (now - lastRefetchTime.current >= cooldownMs) {
        lastRefetchTime.current = now;
        if (callbackRef.current) {
          callbackRef.current();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tryRefetch();
      }
    };

    // [보완] 네이티브(Flutter) 앱이 백그라운드→포그라운드로 전환될 때 주입되는
    // 커스텀 이벤트. flutter_inappwebview는 앱 레벨 포그라운드 전환에서 웹뷰의
    // visibilitychange가 안정적으로 발생하지 않는 알려진 이슈가 있어(GitHub
    // issue #1923), 네이티브가 확실히 감지하는 AppLifecycleState.resumed를
    // 이 이벤트로 브릿지해서 보완한다. 일반 브라우저에선 발생하지 않는 이벤트라
    // 기존 visibilitychange 동작에 영향 없이 안전하게 공존한다. (2026-09-15)
    const handleAppResumed = () => tryRefetch();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('app-resumed', handleAppResumed);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('app-resumed', handleAppResumed);
    };
  }, [cooldownMs]);
};
